import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, getImageUrl, adminDownloadBlob } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoShirtOutline, IoSearchOutline, IoAlertCircleOutline } from 'react-icons/io5';

export default function AdminNeedAttention() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return api('/api/admin/products/needs-attention')
      .then((data) => setItems(Array.isArray(data.items) ? data.items : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, location.key]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const code = String(p.code ?? '').toLowerCase();
      const name = String(p.name ?? '').toLowerCase();
      const idStr = String(p.id ?? '');
      const cat = String(p.category_name ?? '').toLowerCase();
      return code.includes(q) || name.includes(q) || idStr.includes(q) || cat.includes(q);
    });
  }, [items, searchQuery]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filtered.length, rowsPerPage, page]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleExportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await adminDownloadBlob('/api/admin/products/needs-attention/export');
      const d = new Date();
      const fname = `products-need-attention-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Need attention"
        subheader="Products missing images, category, description, rental price, color, or size — fix them so the catalog is complete."
        avatar={<IoAlertCircleOutline size={28} />}
      />
      <CardContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          A product appears here if any check fails: no usable image URL (rows with empty URLs count as missing), uncategorized, description under ~40 characters, no positive rental price row, or missing color/size on the product or in attributes.
          Export CSV is sorted by category with a blank row between categories. Columns named MISSING_* show YES when that item is missing — in Excel use Conditional formatting → Highlight cells → equal to → YES, then choose a red fill.
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Code, name, ID, category…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 260, flex: '1 1 240px', maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearchOutline size={20} />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {filtered.length} product{filtered.length !== 1 ? 's' : ''} need updates
          </Typography>
          <Button size="small" variant="outlined" onClick={load} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={exporting}
            onClick={handleExportCsv}
            sx={{ textTransform: 'none' }}
          >
            {exporting ? 'Exporting…' : 'Export CSV (detailed)'}
          </Button>
        </Box>

        {items.length === 0 && !error ? (
          <AdminEmptyState
            message="No products need attention — catalog data looks complete for these checks."
            icon={IoShirtOutline}
          />
        ) : filtered.length === 0 ? (
          <AdminEmptyState message="No products match your search." icon={IoSearchOutline} />
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'min(70vh, 640px)', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.id}</TableCell>
                      <TableCell>
                        {p.first_image_url ? (
                          <Box
                            component="img"
                            src={getImageUrl(p.first_image_url)}
                            alt=""
                            sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IoShirtOutline size={24} color="#999" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{p.code}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.category_name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(p.issues || []).map((iss) => (
                            <Chip key={iss.code} label={iss.label} size="small" color="warning" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => navigate(`/admin/products/edit/${p.id}`)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={filtered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Rows per page"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
